import { sbEditable } from '@storyblok/storyblok-editable';
import type { ReactElement, RefObject } from 'react';
import { useRef, useState } from 'react';
import type { HeaderTitleH1Props } from './HeaderTitleH1.data';
import styles from './HeaderTitleH1.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './HeaderTitleH1.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import Heading from '../../atoms/heading/Heading';
import classNames from 'classnames';
import useIsomorphicLayoutEffect from '../../../hooks/useIsomorphicLayoutEffect';
import useWindowSize from '../../../hooks/useWindowSize';

export default function HeaderTitleH1({
  isVisible,
  className,
  canTransitionIn,
  blok,
  ref,
}: HeaderTitleH1Props): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const titleRef = useRef<HTMLHeadingElement>(null);

  const [overflowFontsize, setOverflowFontsize] = useState(0);
  const { width } = useWindowSize();

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);

  useIsomorphicLayoutEffect(() => {
    if (!titleRef?.current) return;

    if (titleRef?.current?.scrollWidth <= titleRef?.current?.offsetWidth) {
      setOverflowFontsize(0);
    }
  }, [width]);

  useIsomorphicLayoutEffect(() => {
    if (!titleRef?.current) return;

    const words = titleRef?.current.querySelectorAll(
      '.split-words',
    ) as unknown as Array<HTMLElement>;
    let width = 0;
    words?.forEach((word) => {
      if (word.offsetWidth > width) {
        width = word.offsetWidth;
      }
    });

    if (width > titleRef?.current?.offsetWidth) {
      // if (titleRef?.current?.scrollWidth > titleRef?.current?.offsetWidth) {
      const fontSize = window
        ?.getComputedStyle(titleRef.current!, null)
        .getPropertyValue('font-size');

      setOverflowFontsize((titleRef.current.offsetWidth / width) * parseInt(fontSize, 10));
    }
  }, [titleRef?.current?.offsetWidth, overflowFontsize]);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.headerTitleH1, className)}
      {...sbEditable(blok)}
      style={
        blok.use_accent_color
          ? ({
              background: blok.background_color?.color,
              color: blok.foreground_color?.color,
            } as React.CSSProperties)
          : {}
      }
    >
      <Heading
        as="h1"
        size="heading-01"
        disableTriggerInOnScroll={false}
        canTransitionIn={canTransitionIn}
        transitionInType="splitWordsSlideInUp"
        className={classNames(styles.title, {
          [styles.fontsizeOverflowing]: overflowFontsize,
        })}
        ref={titleRef}
        enableChineseTransitionFallback
        style={
          {
            '--overflowFontsize': overflowFontsize ? `${overflowFontsize}px` : undefined,
          } as React.CSSProperties
        }
      >
        {blok.title}
      </Heading>
    </div>
  );
}
